import * as THREE from "three";

// Riduce le draw call unendo le mesh statiche.
//
// - Mesh con materiali "semplici" (Standard/Basic senza texture né trasparenza) e non protetti:
//   unite anche se hanno colori diversi, spostando il colore nei vertex colors.
// - Tutte le altre: unite solo con mesh che condividono la stessa istanza di materiale,
//   così le modifiche runtime al materiale (opacità, emissive, mappe canvas) continuano a funzionare.
//
// Passata globale: tutto ciò che non sta sotto un nodo dinamico viene cotto in world space.
// Passata locale: dentro i nodi dinamici (robot, drone, gruppi cliccabili...) si uniscono
// solo le mesh foglia sorelle, nello spazio del loro genitore, così le animazioni restano intatte.

const isPlain = (m, protectedMaterials) =>
  (m.isMeshStandardMaterial || m.isMeshBasicMaterial) &&
  !m.isMeshPhysicalMaterial &&
  !protectedMaterials.has(m) &&
  !m.transparent &&
  m.opacity === 1 &&
  !m.map &&
  !m.alphaMap &&
  !m.emissiveMap &&
  !m.normalMap &&
  !m.vertexColors &&
  m.visible;

const recolorKey = (m) =>
  [
    m.type,
    m.flatShading ? 1 : 0,
    m.side,
    m.fog ? 1 : 0,
    m.toneMapped ? 1 : 0,
    m.depthWrite ? 1 : 0,
    m.polygonOffset ? `${m.polygonOffsetFactor},${m.polygonOffsetUnits}` : "",
  ].join("|");

const meshKey = (mesh) => `${mesh.castShadow ? 1 : 0}${mesh.receiveShadow ? 1 : 0}|${mesh.renderOrder}`;

// Estrae un pezzo di geometria (eventualmente un solo gruppo multi-materiale), trasformato e non indicizzato
function extractPiece(geometry, group, matrix, color) {
  let g = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const start = group ? group.start : 0;
  const count = group ? Math.min(group.count, g.attributes.position.count - start) : g.attributes.position.count;

  const out = new THREE.BufferGeometry();
  const copyAttr = (name, itemSize, fallback) => {
    const src = g.attributes[name];
    const arr = new Float32Array(count * itemSize);
    if (src) {
      const getters = [src.getX, src.getY, src.getZ].slice(0, itemSize);
      for (let i = 0; i < count; i++) {
        for (let k = 0; k < itemSize; k++) arr[i * itemSize + k] = getters[k].call(src, start + i);
      }
    } else if (fallback) {
      for (let i = 0; i < count; i++) arr.set(fallback, i * itemSize);
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, itemSize));
  };
  copyAttr("position", 3);
  copyAttr("normal", 3, [0, 1, 0]);
  copyAttr("uv", 2, [0, 0]);
  g.dispose();

  out.applyMatrix4(matrix);

  // Scala negativa: l'ordine dei vertici va invertito, altrimenti le facce risultano girate
  if (matrix.determinant() < 0) {
    ["position", "normal", "uv"].forEach((name) => {
      const a = out.attributes[name];
      const s = a.itemSize;
      for (let i = 0; i + 2 < a.count; i += 3) {
        for (let k = 0; k < s; k++) {
          const tmp = a.array[(i + 1) * s + k];
          a.array[(i + 1) * s + k] = a.array[(i + 2) * s + k];
          a.array[(i + 2) * s + k] = tmp;
        }
      }
    });
  }

  if (color) {
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) colors.set([color.r, color.g, color.b], i * 3);
    out.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  }
  return out;
}

const emissiveTmp = new THREE.Color();
function addPbrAttributes(piece, m) {
  const n = piece.attributes.position.count;
  const pbr = new Float32Array(n * 2);
  const em = new Float32Array(n * 3);
  emissiveTmp.copy(m.emissive).multiplyScalar(m.emissiveIntensity);
  for (let i = 0; i < n; i++) {
    pbr[i * 2] = m.roughness;
    pbr[i * 2 + 1] = m.metalness;
    em.set([emissiveTmp.r, emissiveTmp.g, emissiveTmp.b], i * 3);
  }
  piece.setAttribute("pbr", new THREE.BufferAttribute(pbr, 2));
  piece.setAttribute("emissiveColor", new THREE.BufferAttribute(em, 3));
}

function mergePieces(pieces) {
  let total = 0;
  pieces.forEach((p) => (total += p.attributes.position.count));
  const merged = new THREE.BufferGeometry();
  const names = Object.keys(pieces[0].attributes);
  names.forEach((name) => {
    const itemSize = pieces[0].attributes[name].itemSize;
    const arr = new Float32Array(total * itemSize);
    let offset = 0;
    pieces.forEach((p) => {
      arr.set(p.attributes[name].array, offset);
      offset += p.attributes[name].array.length;
    });
    merged.setAttribute(name, new THREE.BufferAttribute(arr, itemSize));
  });
  merged.computeBoundingSphere();
  merged.computeBoundingBox();
  pieces.forEach((p) => p.dispose());
  return merged;
}

// Materiale standard con roughness / metalness / emissive letti dai vertici:
// così mesh con parametri PBR diversi finiscono in un'unica draw call senza cambiare aspetto.
function makeVertexPbrMaterial(base) {
  const m = base.clone();
  m.color.setRGB(1, 1, 1);
  m.emissive.setRGB(1, 1, 1);
  m.emissiveIntensity = 1;
  m.vertexColors = true;
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nattribute vec2 pbr;\nattribute vec3 emissiveColor;\nvarying vec2 vPbr;\nvarying vec3 vEmissiveColor;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvPbr = pbr;\nvEmissiveColor = emissiveColor;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec2 vPbr;\nvarying vec3 vEmissiveColor;")
      .replace("#include <roughnessmap_fragment>", "float roughnessFactor = vPbr.x;")
      .replace("#include <metalnessmap_fragment>", "float metalnessFactor = vPbr.y;")
      .replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = vEmissiveColor;");
  };
  m.customProgramCacheKey = () => "vertexPbr";
  return m;
}

// Aggiunge i pezzi di una mesh ai bucket; registra l'intervallo di vertici se la mesh è ricolorabile
function collectMesh(mesh, matrix, buckets, protectedMaterials, nameTag, recolorable) {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const groups = Array.isArray(mesh.material) && mesh.geometry.groups.length
    ? mesh.geometry.groups
    : [null];

  groups.forEach((group) => {
    const m = group ? materials[group.materialIndex] : materials[0];
    if (!m) return;
    const plain = isPlain(m, protectedMaterials);
    const key = `${nameTag}|${meshKey(mesh)}|${plain ? "P|" + recolorKey(m) : "I|" + m.uuid}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { plain, material: m, mesh, pieces: [], vertexCount: 0, ranges: [] };
      buckets.set(key, bucket);
    }
    const piece = extractPiece(mesh.geometry, group, matrix, plain ? m.color : null);
    if (plain && m.isMeshStandardMaterial) addPbrAttributes(piece, m);
    if (plain && recolorable.has(mesh)) {
      bucket.ranges.push({ mesh, start: bucket.vertexCount, count: piece.attributes.position.count });
    }
    bucket.vertexCount += piece.attributes.position.count;
    bucket.pieces.push(piece);
  });
}

function buildMeshes(buckets, parent, name, colorTargets) {
  let created = 0;
  buckets.forEach((bucket) => {
    const geometry = mergePieces(bucket.pieces);
    let material = bucket.material;
    if (bucket.plain && bucket.material.isMeshStandardMaterial) {
      material = makeVertexPbrMaterial(bucket.material);
    } else if (bucket.plain) {
      material = bucket.material.clone();
      material.color.setRGB(1, 1, 1);
      material.vertexColors = true;
    }
    if (!material.map && !material.alphaMap) geometry.deleteAttribute("uv");
    const merged = new THREE.Mesh(geometry, material);
    merged.name = bucket.mesh.name.startsWith("interactive") ? bucket.mesh.name : name;
    merged.castShadow = bucket.mesh.castShadow;
    merged.receiveShadow = bucket.mesh.receiveShadow;
    merged.renderOrder = bucket.mesh.renderOrder;
    parent.add(merged);
    bucket.ranges.forEach((r) => colorTargets.set(r.mesh, { attr: geometry.attributes.color, start: r.start, count: r.count }));
    created++;
  });
  return created;
}

// Rimuove le mesh unite; i figli non uniti vengono spostati in un nodo vuoto con la stessa trasformazione
function detachMerged(list, mergedSet) {
  list.forEach((o) => {
    const parent = o.parent;
    if (!parent) return;
    const remaining = o.children.filter((c) => !mergedSet.has(c));
    if (remaining.length) {
      const holder = new THREE.Object3D();
      holder.name = o.name;
      holder.position.copy(o.position);
      holder.quaternion.copy(o.quaternion);
      holder.scale.copy(o.scale);
      parent.add(holder);
      remaining.forEach((c) => holder.add(c));
    }
    parent.remove(o);
  });
}

const canMerge = (o, keep) =>
  o.isMesh &&
  !o.isInstancedMesh &&
  !o.isSkinnedMesh &&
  o.visible &&
  !keep.has(o) &&
  !o.geometry.morphAttributes.position;

/**
 * rigidBodies: nodi che si muovono (o vengono cliccati) come un blocco unico. Le mesh al loro interno
 *   vengono unite nello spazio locale del corpo rigido più vicino. Tutto il resto è statico (world space).
 * keep: mesh da non toccare (referenziate dal codice: animate, raycast, materiali per-mesh).
 * protectedMaterials: materiali modificati a runtime: uniti solo con chi condivide la stessa istanza.
 * recolorable: mesh unite ma ricolorabili via setColor(mesh, hex) (es. muri/pavimento dei preset).
 */
export function optimizeScene(scene, { rigidBodies = [], keep = [], protectedMaterials = [], recolorable = [] } = {}) {
  const keepSet = new Set(keep.filter(Boolean));
  const protectedSet = new Set(protectedMaterials.filter(Boolean));
  const recolorSet = new Set(recolorable.filter(Boolean));
  const bodies = new Set([scene, ...rigidBodies.filter(Boolean)]);
  const colorTargets = new Map();

  scene.updateMatrixWorld(true);

  const bodyOf = (o) => {
    for (let p = o.parent; p; p = p.parent) if (bodies.has(p)) return p;
    return scene;
  };
  // Un corpo rigido nascosto per intero si può unire comunque (la visibilità resta sul corpo)
  const isVisibleChain = (o) => {
    for (let p = o; p && !bodies.has(p); p = p.parent) if (!p.visible) return false;
    return true;
  };

  const perBody = new Map();
  scene.traverse((o) => {
    if (!canMerge(o, keepSet) || !isVisibleChain(o) || bodies.has(o)) return;
    const body = bodyOf(o);
    if (!perBody.has(body)) perBody.set(body, { meshes: [], buckets: new Map() });
    perBody.get(body).meshes.push(o);
  });

  let removed = 0;
  let created = 0;
  const inverse = new THREE.Matrix4();
  const local = new THREE.Matrix4();
  perBody.forEach(({ meshes, buckets }, body) => {
    if (meshes.length < 2 && !meshes.some((m) => recolorSet.has(m))) return;
    inverse.copy(body.matrixWorld).invert();
    meshes.forEach((o) => {
      const nameTag = o.name.startsWith("interactive") ? o.name : "";
      local.multiplyMatrices(inverse, o.matrixWorld);
      collectMesh(o, local, buckets, protectedSet, nameTag, recolorSet);
    });
    // Unire non serve se ogni mesh finisce in un bucket a sé
    if (buckets.size >= meshes.length) {
      buckets.forEach((b) => b.pieces.forEach((p) => p.dispose()));
      return;
    }
    const mergedSet = new Set(meshes);
    detachMerged(meshes, mergedSet);
    meshes.forEach((o) => o.geometry.dispose());
    removed += meshes.length;
    created += buildMeshes(buckets, body, body === scene ? "mergedStatic" : "mergedRigid", colorTargets);
  });

  const tmp = new THREE.Color();
  const setColor = (mesh, hex) => {
    const target = colorTargets.get(mesh);
    if (!target) return false;
    tmp.setHex(hex);
    for (let i = target.start; i < target.start + target.count; i++) target.attr.setXYZ(i, tmp.r, tmp.g, tmp.b);
    target.attr.needsUpdate = true;
    return true;
  };

  return { removed, created, setColor };
}
